import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { formatBytes } from '../lib/format-bytes';
import { formatDownloadedAt } from '../lib/format-downloaded-at';
import { formatTransferValidity } from '../lib/format-expiry';
import { I } from '../lib/icons';
import { useToast } from '../lib/toast-context';
import type { AuthedUser } from '../lib/auth-types';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { Spinner } from '../components/ui/Spinner';
import { useConfirm } from '../components/ui/useConfirm';
import type { TransferStatus } from '../../shared/transfer-constants';

// Semantik der Zähler (stand bisher nur im Code): beides zählt ausgelöste Aktionen
// des Empfängers, keine abgeschlossenen Übertragungen.
const COUNTER_TITLE =
  'Geöffnet = Aufrufe der Transfer-Seite (mit Passwort erst nach richtiger Eingabe). ' +
  'Downloads = ausgelöste Downloads (Klicks), nicht abgeschlossene Übertragungen.';

/**
 * TransferListPanel — „Meine Transfers"-Verwaltungsliste, extrahiert aus der
 * TransferPage in die Nav-Burger (Sprint UI-Redesign 2026-07-16). Self-contained:
 * fetcht selbst (bei Mount + auf das `transfer:changed`-Window-Event, das die
 * Sende-Karte nach Finalize feuert), verwaltet Revoke/Copy/Reveal + eigenen
 * Confirm-Dialog. So bleibt die Sende-Karte (TransferPage) rein auf das Senden
 * fokussiert und die Liste kann im Burger-Menü der Shell leben.
 */

// Event, das die Sende-Karte nach Finalize feuert → Liste refetcht.
export const TRANSFER_CHANGED_EVENT = 'transfer:changed';

export type TransferListItem = {
  id: string;
  status: TransferStatus;
  message: string | null;
  expiresAt: string;
  createdAt: string;
  fileCount: number;
  totalBytes: number;
  // Server-berechnet: kann DIESER User den Link re-kopieren? (Ciphertext da,
  // aktiv, nicht abgelaufen, eigener Transfer). Steuert den Copy-Button.
  hasLink: boolean;
  // Repräsentativer Name (Ordner der ersten Datei, sonst Dateiname) zur
  // Wiedererkennung; null wenn (noch) keine Datei. primaryIsFolder → 📁-Präfix.
  primaryName: string | null;
  primaryIsFolder: boolean;
  // Server-Boolean „ist passwortgeschützt?" (nie der Hash) → 🔒-Kennzeichen.
  hasPassword: boolean;
  // Nutzungs-Zähler. accessCount = /t-Seite geöffnet (nach Passwort-Gate),
  // downloadCount = Download AUSGELÖST (Einzeldatei-Klick oder ZIP-Start) —
  // nicht „abgeschlossen übertragen". Die Unterscheidung ist der Grund, warum
  // beides nebeneinander steht: „geöffnet, aber nichts geladen" ist ein
  // eigenes Signal (Passwort vergessen, falsche Dateien erwartet, …).
  accessCount: number;
  lastAccessedAt: string | null;
  downloadCount: number;
  lastDownloadedAt: string | null;
};

type ListState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ok'; items: TransferListItem[] };

/** Status als dezenter Punkt + farbiger Text (statt gefüllter Pill, User-Feedback
 * 2026-07-16). „Abgelaufen" client-berechnet: der Server lässt status='active'
 * bis der Cleanup-Worker räumt — die UI zeigt expiresAt < now ehrlich an. */
function statusChip(t: TransferListItem): { label: string; textCls: string; dotCls: string } {
  if (t.status === 'draft') {
    return { label: 'Entwurf', textCls: 'text-brand-green-600', dotCls: 'bg-ink-300' };
  }
  if (t.status === 'revoked') {
    return { label: 'Widerrufen', textCls: 'text-red-600 font-medium', dotCls: 'bg-red-500' };
  }
  if (new Date(t.expiresAt).getTime() < Date.now()) {
    return { label: 'Abgelaufen', textCls: 'text-brand-green-500', dotCls: 'bg-ink-300' };
  }
  return { label: 'Aktiv', textCls: 'text-brand-green-700 font-medium', dotCls: 'bg-brand-green-500' };
}

export function TransferListPanel({ authedUser }: { authedUser: AuthedUser }) {
  const toast = useToast();
  const { confirm, confirmDialog } = useConfirm();
  const [listState, setListState] = useState<ListState>({ status: 'loading' });
  const [refetchKey, setRefetchKey] = useState(0);
  const [revokingIds, setRevokingIds] = useState<Set<string>>(() => new Set());
  const [copyingLinkIds, setCopyingLinkIds] = useState<Set<string>>(() => new Set());
  // Inline sichtbarer Link nach „Link kopieren" — robuster Fallback, falls die
  // Clipboard-API nach dem Fetch-await keine Berechtigung mehr hat.
  const [revealedLink, setRevealedLink] = useState<{ id: string; url: string } | null>(null);
  const toastRef = useRef(toast);
  toastRef.current = toast;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api<{ transfers: TransferListItem[] }>('/api/transfers');
        if (!cancelled) setListState({ status: 'ok', items: res.transfers });
      } catch (err) {
        if (!cancelled) {
          setListState({
            status: 'error',
            message: err instanceof Error ? err.message : 'Laden fehlgeschlagen',
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refetchKey]);

  // Refresh, wenn die Sende-Karte einen neuen Transfer finalisiert hat.
  useEffect(() => {
    const onChanged = () => setRefetchKey((k) => k + 1);
    window.addEventListener(TRANSFER_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(TRANSFER_CHANGED_EVENT, onChanged);
  }, []);

  const handleCopyListLink = async (t: TransferListItem): Promise<void> => {
    if (copyingLinkIds.has(t.id)) return;
    setCopyingLinkIds((prev) => new Set(prev).add(t.id));
    try {
      const res = await api<{ url: string }>(
        `/api/transfers/${encodeURIComponent(t.id)}/link`,
      );
      const fullUrl = `${window.location.origin}${res.url}`;
      setRevealedLink({ id: t.id, url: fullUrl });
      try {
        await navigator.clipboard.writeText(fullUrl);
        toastRef.current.success('Transfer-Link kopiert');
      } catch {
        toastRef.current.info('Link unten zum Kopieren markiert');
      }
    } catch (err) {
      toastRef.current.error(
        err instanceof Error ? err.message : 'Link konnte nicht abgerufen werden',
      );
    } finally {
      setCopyingLinkIds((prev) => {
        const next = new Set(prev);
        next.delete(t.id);
        return next;
      });
    }
  };

  const handleRevoke = async (t: TransferListItem): Promise<void> => {
    if (revokingIds.has(t.id)) return;
    const isDraft = t.status === 'draft';
    const ok = await confirm({
      title: isDraft ? 'Entwurf löschen?' : 'Transfer widerrufen?',
      message: isDraft
        ? 'Der Entwurf und seine Dateien werden endgültig gelöscht.'
        : 'Der Link ist danach sofort ungültig. Bereits gestartete Downloads laufen bis zu 5 Minuten weiter.',
      confirmLabel: isDraft ? 'Löschen' : 'Widerrufen',
      variant: 'danger',
    });
    if (!ok) return;
    setRevokingIds((prev) => new Set(prev).add(t.id));
    try {
      await api<{ revoked: true }>(`/api/transfers/${encodeURIComponent(t.id)}`, {
        method: 'DELETE',
      });
      toastRef.current.success(isDraft ? 'Entwurf gelöscht' : 'Transfer widerrufen');
      // Einen evtl. inline angezeigten Link dieser Zeile mit-entfernen (nach
      // Revoke tot, Server nullt den Ciphertext).
      setRevealedLink((r) => (r?.id === t.id ? null : r));
      setRefetchKey((k) => k + 1);
    } catch (err) {
      toastRef.current.error(err instanceof Error ? err.message : 'Aktion fehlgeschlagen');
    } finally {
      setRevokingIds((prev) => {
        const next = new Set(prev);
        next.delete(t.id);
        return next;
      });
    }
  };

  return (
    <div>
      <h2 className="text-lg font-medium text-brand-green-800 mb-1">Meine Transfers</h2>
      <p className="text-xs2 font-mono text-brand-green-600 mb-4">
        Aktive Links kannst du jederzeit erneut kopieren
        {authedUser.isAdmin ? ' · als Admin siehst du die Transfers aller Nutzer' : ''}.
      </p>

      {listState.status === 'loading' && (
        <div className="py-10 flex justify-center">
          <Spinner />
        </div>
      )}
      {listState.status === 'error' && (
        <div className="px-4 py-3 bg-white border border-red-300 rounded-md text-sm text-red-700">
          {listState.message}
        </div>
      )}
      {listState.status === 'ok' && listState.items.length === 0 && (
        <EmptyState
          icon={<I.package size={20} />}
          title="Noch keine Transfers"
          sub="Wähle Dateien aus und erstelle deinen ersten Transfer-Link."
        />
      )}
      {listState.status === 'ok' && listState.items.length > 0 && (
        <ul className="space-y-2">
          {listState.items.map((t) => {
            const chip = statusChip(t);
            const busy = revokingIds.has(t.id);
            return (
              <li
                key={t.id}
                className="flex flex-col gap-2 px-3 py-2 bg-white border border-ink-200 rounded-md"
              >
                {/* Zeile 1: Datei-/Byte-Info. Zeile 2: Status (Punkt + Text) +
                    Gültigkeit + optionale Nachricht. Aktionen als Textlinks darunter
                    (User-Feedback 2026-07-16: weniger dominant als Pill + Buttons). */}
                <div className="min-w-0">
                  <span className="flex min-w-0 items-baseline gap-1.5 text-sm">
                    {t.primaryName ? (
                      <>
                        {/* Name (Ordner/Datei) truncatet; Zähler/Größe bleiben sichtbar. */}
                        <span className="min-w-0 truncate font-medium text-brand-green-800">
                          {t.primaryIsFolder ? '📁 ' : ''}
                          {t.primaryName}
                        </span>
                        <span className="flex-shrink-0 text-brand-green-600">
                          {t.fileCount > 1 ? ` · ${t.fileCount} Dateien` : ''} ·{' '}
                          {formatBytes(t.totalBytes)}
                        </span>
                      </>
                    ) : (
                      <span className="text-brand-green-700">
                        {t.fileCount} {t.fileCount === 1 ? 'Datei' : 'Dateien'} ·{' '}
                        {formatBytes(t.totalBytes)}
                      </span>
                    )}
                  </span>
                  <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-2xs font-mono">
                    <span className="inline-flex items-center gap-1">
                      <span className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${chip.dotCls}`} />
                      <span className={chip.textCls}>{chip.label}</span>
                    </span>
                    {t.hasPassword ? (
                      <span className="text-brand-green-600" title="Passwortgeschützt">
                        · 🔒
                      </span>
                    ) : null}
                    <span className="text-brand-green-600">
                      · {formatTransferValidity(t.expiresAt)}
                    </span>
                    {t.message ? (
                      <span className="min-w-0 truncate text-brand-green-600">· „{t.message}“</span>
                    ) : null}
                  </span>
                  {/* Nutzungs-Zähler. Drafts sind nie öffentlich erreichbar →
                      dort wären beide Zahlen konstant 0 und damit reines
                      Rauschen. Bei revoked/expired bleibt die Zeile bewusst
                      stehen: gerade dort ist „wurde nie geladen" die
                      wertvollste Information. */}
                  {t.status !== 'draft' && (
                    <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-2xs font-mono text-brand-green-600">
                      <span title={COUNTER_TITLE}>
                        👁 {t.accessCount}× geöffnet · ↓ {t.downloadCount}{' '}
                        {t.downloadCount === 1 ? 'Download' : 'Downloads'}
                        {t.downloadCount > 0 && formatDownloadedAt(t.lastDownloadedAt)
                          ? ` · zuletzt ${formatDownloadedAt(t.lastDownloadedAt)}`
                          : ''}
                      </span>
                      {t.accessCount > 0 && t.downloadCount === 0 ? (
                        <span
                          className="text-brand-green-500"
                          title="Der Link wurde geöffnet, aber es wurde nichts heruntergeladen."
                        >
                          ⚠ gesehen, aber nichts geladen
                        </span>
                      ) : null}
                    </span>
                  )}
                </div>
                {(t.hasLink || t.status === 'draft' || t.status === 'active') && (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    {t.hasLink && (
                      <Button
                        variant="link"
                        disabled={copyingLinkIds.has(t.id)}
                        onClick={() => void handleCopyListLink(t)}
                      >
                        Link kopieren
                      </Button>
                    )}
                    {(t.status === 'draft' || t.status === 'active') && (
                      <Button
                        variant="link"
                        className="text-red-600 hover:text-red-700"
                        disabled={busy}
                        onClick={() => void handleRevoke(t)}
                      >
                        {t.status === 'draft' ? 'Löschen' : 'Widerrufen'}
                      </Button>
                    )}
                  </div>
                )}
                {revealedLink?.id === t.id && (
                  <code className="block w-full min-w-0 truncate px-2 py-1.5 bg-brand-cream border border-ink-200 rounded text-xs font-mono text-brand-green-700">
                    {revealedLink.url}
                  </code>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {confirmDialog}
    </div>
  );
}
